import { OrderListResponseType } from "@sellar-io/api-v2/types";
import { ReactElement } from "react";
import { OrderAcceptedBadge } from "../../../components/badge/OrderAcceptedBadge";
import { OrderCancelledBadge } from "../../../components/badge/OrderCancelledBadge";
import { OrderConfirmedBadge } from "../../../components/badge/OrderConfirmedBadge";
import { OrderDeliveredBadge } from "../../../components/badge/OrderDeliveredBadge";
import { OrderDispatchedBadge } from "../../../components/badge/OrderDispatchedBadge";
import { OrderRequestedBadge } from "../../../components/badge/OrderRequestedBadge";
import { OrderVoidedBadge } from "../../../components/badge/OrderVoidedBadge";
import { TooltipContainer } from "@sellar-io/components";
import { Badge } from "../../../components/badge/Badge";

export default function OrderStatusIndicator({
  order,
  iconOnly = false,
}: {
  order: OrderListResponseType[number];
  iconOnly?: boolean;
}): ReactElement | null {
  if (!order) return null;

  if (iconOnly) {
    if (order.voidedAt) {
      return (
        <TooltipContainer enable text="Order voided">
          <Badge icon="CLOSE" className="bg-zinc-100 text-zinc-700" />
        </TooltipContainer>
      );
    }

    if (order.deliveredAt) {
      return (
        <TooltipContainer
          enable={true}
          text={
            order.fulfillmentType === "collection" ? "collected" : "delivered"
          }
        >
          <Badge icon="CHECK_ALL" className="bg-blue-100 text-blue-700" />
        </TooltipContainer>
      );
    }

    if (order.dispatchedAt) {
      return (
        <TooltipContainer enable={true} text="Order dispatched">
          <Badge
            icon={"PACKAGE_VARIANT_CLOSED"}
            className="bg-purple-100 text-purple-700"
          />
        </TooltipContainer>
      );
    }

    if (order.deniedAt || order.cancelledAt) {
      return (
        <TooltipContainer enable={true} text="Order cancelled">
          <Badge icon={"CLOSE"} className="bg-red-100 text-red-700" />
        </TooltipContainer>
      );
    }

    if (order.confirmedAt) {
      return (
        <TooltipContainer enable={true} text="Order finalised">
          <Badge
            icon={"CHECK_CIRCLE"}
            className="bg-emerald-100 text-emerald-700"
          />
        </TooltipContainer>
      );
    }

    if (order.acceptedAt) {
      return (
        <TooltipContainer enable={true} text="Order accepted">
          <Badge
            icon={"THUMB_UP_OUTLINE"}
            className="bg-orange-100 text-orange-800"
          />
        </TooltipContainer>
      );
    }

    return (
      <TooltipContainer enable={true} text="Order requested">
        <Badge
          icon={"TIMER_SAND_EMPTY"}
          className="bg-yellow-100 text-yellow-800"
        />
      </TooltipContainer>
    );
  } else {
    if (order.voidedAt) {
      return <OrderVoidedBadge />;
    }

    if (order.deliveredAt) {
      return (
        <OrderDeliveredBadge
          type={
            order.fulfillmentType === "collection" ? "collected" : "delivered"
          }
        />
      );
    }

    if (order.dispatchedAt) {
      return <OrderDispatchedBadge />;
    }

    if (order.deniedAt || order.cancelledAt) {
      return <OrderCancelledBadge />;
    }

    if (order.confirmedAt) {
      return <OrderConfirmedBadge />;
    }

    if (order.acceptedAt) {
      return <OrderAcceptedBadge />;
    }

    return <OrderRequestedBadge />;
  }
}
